import {
  HistoryContent,
  HistoryDeletePanel,
  HistoryHeader,
  HistoryPanel,
  HistoryPanelItem,
  HistoryPanelItems,
  HistoryPanelMenu,
  HistoryShell,
  HistoryToolbar, // Explicit /index.js: the package's exports map is `./es/*` with no directory
  // index resolution, so the extensionless path IBM's example uses fails here.
} from '@carbon/ai-chat-components/es/react/history/index.js';
import TrashCan16 from '@carbon/icons/es/trash-can/16.js';
import iconLoader from '@carbon/web-components/es/globals/internal/icon-loader.js';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  agentConversationsAtomFamily,
  agentRenamingConversationAtomFamily,
} from 'atoms/agent-chat-atoms';

import {
  deleteAgentConversation,
  fetchAgentConversations,
  renameAgentConversation,
} from '../api';
import type { AgentSubject, ConversationListItem } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

// Recency buckets, matching the sections Carbon's own history example uses.
const SECTIONS = [
  { label: 'Today', maxAgeMs: DAY_MS },
  { label: 'Yesterday', maxAgeMs: 2 * DAY_MS },
  { label: 'Previous 7 days', maxAgeMs: 7 * DAY_MS },
  { label: 'Older', maxAgeMs: Infinity },
] as const;

// Carbon caps rename input length in its example; keep the same ceiling so the
// server never rejects a title the UI accepted.
const MAX_TITLE_LENGTH = 75;

const titleFor = (conversation: ConversationListItem) =>
  conversation.title?.trim() || `Conversation ${conversation.id}`;

function sectionsFor(conversations: ConversationListItem[]) {
  const now = Date.now();
  return SECTIONS.map((section, index) => {
    const min = index === 0 ? 0 : SECTIONS[index - 1].maxAgeMs;
    return {
      label: section.label,
      items: conversations.filter((conversation) => {
        const age = now - new Date(conversation.updatedAt).getTime();
        return age >= min && age < section.maxAgeMs;
      }),
    };
  }).filter((section) => section.items.length > 0);
}

// Contents of Carbon's history panel, assembled from Carbon's own history
// components. The shell, header, toolbar, rows, rename input, overflow menu and
// delete confirmation are all the library's — this supplies our conversations
// and points the actions at our endpoints.
//
// Structure mirrors IBM's `history-fullscreen` React example:
// HistoryShell → HistoryHeader / HistoryToolbar / HistoryContent →
// HistoryPanel → HistoryPanelItems → HistoryPanelMenu → HistoryPanelItem.
export const ConversationList = ({
  agentType,
  scopeKey,
  subject,
  activeConversationId,
  onSelect,
  onDeleteActive,
  onNewChat,
  onClose,
  registerRefresh,
}: {
  agentType: string;
  /** Namespaces the rows by agent + record — see `agentScopeKey`. */
  scopeKey: string;
  /** Limits the list to this record's threads. */
  subject?: AgentSubject;
  activeConversationId: number | null;
  onSelect: (conversationId: number) => void;
  /** Called when the thread being displayed is the one deleted. */
  onDeleteActive: () => void;
  /** Toolbar's new-chat button. */
  onNewChat: () => void;
  /** Closes Carbon's history panel — wired to its header close button. */
  onClose: () => void;
  /** Hands the refresh callback up so the panel can refetch when it opens. */
  registerRefresh?: (refresh: () => void) => void;
}) => {
  const [conversations, setConversations] = useAtom(
    agentConversationsAtomFamily(scopeKey)
  );
  const [renamingId, setRenamingId] = useAtom(
    agentRenamingConversationAtomFamily(scopeKey)
  );
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  // Carbon mounts this inside a portal it can tear down at any time, so a
  // fetch in flight must not write state afterwards.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchAgentConversations(agentType, subject);
      if (mounted.current) setConversations(next);
    } catch {
      // Soft feature: a failed list should never block the chat itself.
    }
  }, [agentType, subject, setConversations]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The panel stays mounted for the life of the chat, so without this a thread
  // created (or auto-titled) during this session never appears.
  useEffect(() => {
    registerRefresh?.(() => void refresh());
  }, [refresh, registerRefresh]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((conversation) =>
      titleFor(conversation).toLowerCase().includes(term)
    );
  }, [conversations, search]);

  const commitRename = useCallback(
    async (conversation: ConversationListItem, title: string) => {
      setRenamingId(null);
      const trimmed = title.trim().slice(0, MAX_TITLE_LENGTH);
      if (!trimmed || trimmed === conversation.title) return;
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== conversation.id) return c;
          return { ...c, title: trimmed };
        })
      );
      try {
        await renameAgentConversation(conversation.id, trimmed);
      } catch {
        void refresh();
      }
    },
    [refresh, setConversations, setRenamingId]
  );

  const confirmDelete = useCallback(async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (id === null) return;
    setConversations((prev) => prev.filter((c) => c.id !== id));
    // Otherwise the transcript keeps showing a deleted thread and the next
    // send posts against its id.
    if (id === activeConversationId) onDeleteActive();
    try {
      await deleteAgentConversation(id);
    } catch {
      void refresh();
    }
  }, [
    activeConversationId,
    onDeleteActive,
    pendingDeleteId,
    refresh,
    setConversations,
  ]);

  // Display only — the component reports clicks through onMenuAction rather
  // than calling anything on these entries.
  //
  // `icon` is interpolated straight into a Lit child part, so it must be
  // rendered SVG: a React element commits as text, which both prints
  // "[object Object]" and corrupts the action name (the click handler falls
  // back to the item's textContent). Only Delete carries an icon, matching the
  // reference — a second directive result in the same repeat does not render.
  const itemActions = useMemo(
    () => [
      { text: 'Rename' },
      {
        text: 'Delete',
        icon: iconLoader(TrashCan16, { slot: 'icon' }),
        delete: true,
        divider: true,
      },
    ],
    []
  );

  const sections = sectionsFor(visible);

  return (
    <HistoryShell>
      <HistoryHeader
        slot="header"
        headerTitle="Chats"
        showCloseAction
        closeButtonLabel="Close chats"
        onClose={onClose}
      />
      <HistoryToolbar
        slot="toolbar"
        newChatLabel="New chat"
        searchAttributes={{ placeholder: 'Search chats' }}
        onNewChatClick={onNewChat}
        onSearchInput={(event: CustomEvent<{ value?: string }>) =>
          setSearch(event.detail?.value ?? '')
        }
      />
      <HistoryContent
        slot="content"
        resultsLabel="Results"
        resultsCount={search ? visible.length : undefined}
      >
        <HistoryPanel aria-label="Chat history">
          <HistoryPanelItems>
            {sections.map((section) => (
              <HistoryPanelMenu
                key={section.label}
                title={section.label}
                expanded
              >
                {section.items.map((conversation) => (
                  <HistoryPanelItem
                    key={conversation.id}
                    id={String(conversation.id)}
                    name={titleFor(conversation)}
                    selected={conversation.id === activeConversationId}
                    rename={renamingId === conversation.id}
                    overflowMenuLabel="Conversation actions"
                    actions={itemActions}
                    onSelected={() => onSelect(conversation.id)}
                    onMenuAction={(event: CustomEvent<{ action?: string }>) => {
                      if (event.detail?.action === 'Rename') {
                        setRenamingId(conversation.id);
                      }
                      if (event.detail?.action === 'Delete') {
                        setPendingDeleteId(conversation.id);
                      }
                    }}
                    onRenameSave={(event: CustomEvent<{ newName?: string }>) =>
                      void commitRename(
                        conversation,
                        event.detail?.newName ?? ''
                      )
                    }
                    onRenameCancel={() => setRenamingId(null)}
                  />
                ))}
              </HistoryPanelMenu>
            ))}
          </HistoryPanelItems>
        </HistoryPanel>
      </HistoryContent>

      {pendingDeleteId !== null && (
        <HistoryDeletePanel
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => void confirmDelete()}
        >
          <span slot="title">Delete chat?</span>
          <span slot="description">
            This removes the conversation and its messages. It cannot be undone.
          </span>
        </HistoryDeletePanel>
      )}
    </HistoryShell>
  );
};
